import React from 'react';

import PropTypes from 'prop-types';

import { Collapse } from 'wanderu-animations';
import { CaretIcon } from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import FilterEvents from '../events';
import StationsList from './StationsList/StationsList';
import Events from './events/StationFilterEvents';
import {
    ARRIVAL_TYPE,
    DEPARTURE_TYPE,
} from '../../../constants';

import styles from './StationsFilter.scss';

const getSelectedAndUnSelectedStations = (stations) => stations.reduce(
    (stationSet, { id: stationId, isActive }) => {
        const stationKey = isActive
            ? 'selectedStations'
            : 'unSelectedStations';
        return {
            ...stationSet,
            [stationKey]: [
                ...stationSet[stationKey],
                stationId,
            ],
        };
    },
    {
        selectedStations: [],
        unSelectedStations: [],
    },
);

class StationsFilter extends React.Component {
    static propTypes = {
        arriveStations: PropTypes.arrayOf(PropTypes.object).isRequired,
        departStations: PropTypes.arrayOf(PropTypes.object).isRequired,
        isArrivalStationsFiltered: PropTypes.bool.isRequired,
        isDepartureStationsFiltered: PropTypes.bool.isRequired,
        resetArrivalStations: PropTypes.func.isRequired,
        resetDepartureStations: PropTypes.func.isRequired,
        setArrivalStationActive: PropTypes.func.isRequired,
        setArrivalStationOnlyActive: PropTypes.func.isRequired,
        setDepartureStationActive: PropTypes.func.isRequired,
        setDepartureStationOnlyActive: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
        totalTrips: PropTypes.number.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            isOpen:
                props.arriveStations.length > 0
                || props.departStations.length > 0,
        };
    }

    toggleRow = () => {
        const eventFunction = this.state.isOpen
            ? FilterEvents.filterCollapsed
            : FilterEvents.filterExpanded;

        eventFunction({
            filterType: 'stations',
        });

        if (
            this.props.arriveStations.length > 0
            || this.props.departStations.length > 0
        ) {
            this.setState((currentState) => ({
                isOpen: !currentState.isOpen,
            }));
        }
    };

    onToggleStation = (stationId, isActive, direction) => {
        const {
            arriveStations,
            departStations,
            setArrivalStationActive,
            setDepartureStationActive,
            totalTrips,
        } = this.props;

        let stations = departStations;
        let setStationActive = setDepartureStationActive;
        if (direction === ARRIVAL_TYPE) {
            stations = arriveStations;
            setStationActive = setArrivalStationActive;
        }
        let { selectedStations, unSelectedStations } = getSelectedAndUnSelectedStations(stations);
        const selectedStationsBefore = [...selectedStations];

        if (isActive) {
            selectedStations.push(stationId);
            unSelectedStations = unSelectedStations.filter((station) => station !== stationId);
        } else {
            unSelectedStations.push(stationId);
            selectedStations = selectedStations.filter((station) => station !== stationId);
        }

        setStationActive(stationId, isActive);
        Events.stationChecked({
            action: isActive
                ? 'select'
                : 'unselect',
            direction,
            selectedStations: selectedStations.sort(),
            selectedStationsBefore,
            totalTrips,
            unSelectedStations: unSelectedStations.sort(),
        });
    };

    onToggleOnlyStation = (stationId, direction) => {
        const {
            arriveStations,
            departStations,
            setArrivalStationOnlyActive,
            setDepartureStationOnlyActive,
            totalTrips,
        } = this.props;

        let stations = departStations;
        let setStationOnlyActive = setDepartureStationOnlyActive;
        if (direction === ARRIVAL_TYPE) {
            stations = arriveStations;
            setStationOnlyActive = setArrivalStationOnlyActive;
        }
        let unSelectedStations = stations.reduce((stationSet, station) => {
            if (station.id !== stationId) return stationSet.concat([station.id]);
            return stationSet;
        }, []);

        setStationOnlyActive(stationId);
        Events.stationChecked({
            action: 'exclusive',
            direction,
            selectedStations: [stationId],
            totalTrips,
            unSelectedStations: unSelectedStations.sort(),
        });
    };

    onResetStations = (direction) => {
        const {
            arriveStations,
            departStations,
            resetArrivalStations,
            resetDepartureStations,
            totalTrips,
        } = this.props;

        let stations = departStations;
        let resetStations = resetDepartureStations;
        if (direction === ARRIVAL_TYPE) {
            stations = arriveStations;
            resetStations = resetArrivalStations;
        }
        const unSelectedStations = [];
        const selectedStations = stations.map((station) => station.id);
        resetStations();

        Events.stationChecked({
            action: 'reset',
            direction,
            selectedStations: selectedStations.sort(),
            totalTrips,
            unSelectedStations: unSelectedStations.sort(),
        });

        FilterEvents.singleFilterReset({
            filterType: `${direction}_stations`,
        });
    };

    render() {
        const {
            arriveStations,
            departStations,
            isArrivalStationsFiltered,
            isDepartureStationsFiltered,
            t,
        } = this.props;

        let { isOpen } = this.state;
        isOpen = isOpen && !!arriveStations.length && !!departStations.length;
        return (
            <div className={styles.container}>
                <span
                    className={styles.toggleRow}
                    data-id="toggleRow"
                    onClick={this.toggleRow}
                    role="button"
                >
                    <span className={styles.title}>
                        {t('locations-filter-header')}
                    </span>

                    <CaretIcon showUpArrow={isOpen} />
                </span>
                <Collapse
                    className={styles.collapse}
                    duration={500}
                    isOpen={isOpen}
                >
                    <StationsList
                        direction={DEPARTURE_TYPE}
                        isFiltered={isDepartureStationsFiltered}
                        resetFilters={this.onResetStations}
                        setStationActive={this.onToggleStation}
                        setStationOnlyActive={this.onToggleOnlyStation}
                        stations={departStations}
                        title={t('departure')}
                    />
                    <StationsList
                        direction={ARRIVAL_TYPE}
                        isFiltered={isArrivalStationsFiltered}
                        resetFilters={this.onResetStations}
                        setStationActive={this.onToggleStation}
                        setStationOnlyActive={this.onToggleOnlyStation}
                        stations={arriveStations}
                        title={t('arrival')}
                    />
                </Collapse>
            </div>
        );
    }
}

export { StationsFilter, getSelectedAndUnSelectedStations };
export default localize(StationsFilter, ['t'], 'baseApp');
